<template>
  <PageError v-if="error"
   :error="error"
   :errorCode="errorCode"
  />

  <HowToUser v-else-if="showHowToUser"
    :external-navigation="true"
    :show-how-to-user="showHowToUser"
    @show-how-to-close="handleShowHowToUser"
  />

  <div v-else-if="canView || user?.is_staff" class="p-6 dark:text-white">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Users - {{ filteredUsers.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>

      <div class="flex gap-4">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="showHowToUser=true"
        >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark-icon lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
        <button
        v-if="canAdd || user?.is_staff"
          @click="$router.push({ name: 'AddUser' })"
          class="btn bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add User
        </button>
      </div>
      
    </div>

    <!-- Search and Filter Section -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
    >
      <!-- Search Bar -->
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search users by username..."
        />
      </div>

      <!-- Bulk Action Section -->
      <div class="flex items-center mt-4 md:mt-0 space-x-2">
        <GlassSelect
          v-model="bulkAction"
          :options="canDelete || user?.is_staff ? [{ value: 'delete', label: 'Delete selected items' }] : []"
          placeholder="Bulk Actions"
          :valueKey="'value'"
          :labelKey="'label'"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedUsers.length === 0"
          :class="[
            'btn focus:outline-none focus:ring-2 focus:ring-blue-500',
            bulkAction && selectedUsers.length
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- Users Table -->
    <GenericTable
      ref="table"
      :columns="columns"
      :items="paginatedUsers"
      itemKey="id"
      :selectable="true"
      v-model="selectedUsers"
      @update:selectedItems="updateSelectedUsers"
      @rowClick="goToUserDetail"
    >
        <!-- Custom cell templates -->
        <template #role="{ value }">
          {{ value ? value.charAt(0).toUpperCase() + value.slice(1) : "-" }}
      </template>
      <template #is_active="{ value }">
        {{ value ? "Yes" : "No" }}
      </template>
      <template #last_login="{ value }">
        {{ formatDateTime(value) }}
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
          :valueKey="'value'"
          :labelKey="'label'"
          placeholder="Items per page"
          @change="updatePagination"
        />
      </div>
    </div>

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="selectedObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem=false"
      :isChildObject="true"
      :count="count"
    />

  </div>
</template>

<script>
import {
  getAllUsers,
  deleteUser,
  bulkDeleteUsers,
} from "@/services/userService";
import { getAllOrgs } from "@/services/orgService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import PageError from "@/components/PageError.vue";
import { formatError } from "@/utils/errorMessages";
import { useStore } from "@/store";
import HowToUser from "./HowToUser.vue";
import DeleteModal from "@/components/DeleteModal.vue";
import GlassSelect from "@/components/GlassSelect.vue";

export default {
  name: "UserView",
  components: {
    SearchBar,
    GenericTable,
    PageError,
    HowToUser,
    DeleteModal,
    GlassSelect,
  },
  data() {
    return {
      showHowToUser: false,
      users: [],
      organizations: {},  
      searchQuery: "",
      filteredUsers: [],
      currentPage: 1,
      itemsPerPage: 20,
      selectedUsers: [],
      bulkAction: "",
      columns: [
        { key: "username", label: "Username", sortable: true, width: "17%" },
        {
          key: "org_name",
          label: "Organisation",
          sortable: true,
          width: "15%",
        },
        { key: "role", label: "Role", sortable: true, width: "15%" },
        { key: "is_active", label: "Active", sortable: true, width: "10%" },
        {
          key: "last_login",
          label: "Last Login",
          sortable: true,
          width: "20%",
        },
      ],
      deletingItem: false,
      canAdd: false,
      canDelete: false,
      canView: false,
      error: "",
      errorCode: "",
      isDeleting: false
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    }, 
    isFiltered() {
      return this.searchQuery !== "";
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredUsers.length / this.itemsPerPage);
    },
    paginatedUsers() {
      if (this.itemsPerPage === -1) {
        return this.filteredUsers;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredUsers.slice(start, end);
    },
    selectedObjects(){
        return this.selectedUsers.map(user => ({
          name: "User",
          value: user.username,
       }));
    },
    count(){
      return [{name: "Users", value: this.selectedObjects.length}]
    }
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    },
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true
    },
  },
  mounted() {
    this.fetchOrgs();
    this.fetchUsers();
    this.checkPermissions();
  },
  methods: {
    handleShowHowToUser(value){
      this.showHowToUser = value;
    },
    async checkPermissions() {
      if (this.user) {
        this.canAdd = await useStore.hasPermission("Can add user");
        this.canDelete = await useStore.hasPermission("Can delete user");
        this.canView = await useStore.hasPermission("Can view user");
      }
    },
    async fetchUsers() {
      try {
        // Fetch users and map organization names efficiently
        const response = await getAllUsers();
        this.users = response;
        this.applyFilters();
      } catch (error) {
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      }
    },
    async fetchOrgs(){
      // Fetch all organizations first and create a lookup map
      try {
        const orgs = await getAllOrgs();
        this.organizations = orgs.reduce((acc, org) => {
          acc[org.id] = org.name;
          return acc;
        }, {});
      } catch (error) {
        console.error("Failed to fetch users:", error);
      }
    },
    goToUserDetail(user) {
      this.$router.push({
        name: "UserDetails",
        params: { id: user.id },
      });
    },
    applyFilters() {
      this.filteredUsers = this.users.filter(
        (user) =>
          user.username
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase()) ||
          user.org_name
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase())
      );
      this.currentPage = 1;
    },
    updateSelectedUsers(selectedItems) {
      this.selectedUsers = [...selectedItems];
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete" ) {
        this.deletingItem = true;
      }
      this.bulkAction = "";
    },
    async confirmDelete() {
      this.isDeleting = true;
      await this.deleteSelectedUsers();
      this.deletingItem = false; 
      this.bulkAction = "";
    },
    async deleteSelectedUsers() {
      try {
        await bulkDeleteUsers(this.selectedUsers.map((user) => user.id));
        await this.fetchUsers();
        this.$refs.table.clearSelection();
        this.selectedUsers = [];
        this.bulkAction = "";
      } catch (error) {
        console.error("Error deleting users:", error);
      } finally {
        this.isDeleting = false;
      }
    },
    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    async deleteUserItem(id) {
      if (confirm("Are you sure you want to delete this user?")) {
        try {
          await deleteUser(id);
          this.users = this.users.filter((user) => user.id !== id);
          this.applyFilters(); // Re-apply filters to update the view
        } catch (error) {
          console.error("Error deleting user:", error);
          // Handle error (e.g., show error message to user)
        }
      }
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
  },
};
</script>
